import {CBARProcessNode} from "./CBARProcessNode";
import {CBARFrame} from "../CBARFrame";
import {CBARPipeline} from "./CBARPipeline";
import {RenderOptions} from "../../components";
import '@tensorflow/tfjs-backend-webgl';
import * as deeplab from "@tensorflow-models/deeplab";
import {ModuleThread, spawn, Thread, Worker} from "threads";
import {CBARSegmenter} from "../workers";
import {usleep} from "../../../backend";
import {CBARSurfaceSpawnerNode} from "./CBARSurfaceSpawnerNode";
import {describeImageInput, deserializeImage, matRelease, matRetain} from "../Utils";
import {imShow} from "../../CBARView";

const loadModel = async () => {
    const modelName = 'ade20k';   // set to your preferred model, either `pascal`, `cityscapes` or `ade20k`
    const quantizationBytes = 2;  // either 1, 2 or 4
    return await deeplab.load({base: modelName, quantizationBytes});
};

export class CBARSegmentationNode extends CBARProcessNode {

    private _segmenter?:ModuleThread<CBARSegmenter>;
    private _modelBusy = false;

    constructor(context:CBARPipeline) {
        super(context)

        this.wireModelThread().then(()=>{
            console.log("Segmenter thread now loaded");
        });
    }

    private async wireModelThread() {
        this._modelBusy = true;
        console.log("Wiring segmentation thread");
        this._segmenter = await spawn<CBARSegmenter>(new Worker("./CBARSegmenter"));
        await this._segmenter.initialize();
        await usleep(3000);//opencv load time
        console.log("segmentation loaded");
        this._modelBusy = false;
    }

    private async unwireModelThread() {
        if (this._segmenter) {
            await this._segmenter.destroy();
            await Thread.terminate(this._segmenter);
        }
    }

    update(frame:CBARFrame, renderOptions:RenderOptions):void {

        if (this._modelBusy || !this._segmenter) return;

        const wall = this.pipeline.getNode(CBARSurfaceSpawnerNode).walls.find(wall=>{
            const age = wall.maskFrameIndex ? frame.index - wall.maskFrameIndex : 10000;
            return age > 30;
        });

        if (!wall) return;

        this._modelBusy = true;
        const initialPosition = {... wall.seedPoint};

        const service = this._segmenter;

        const image = matRetain(frame.rgbImage);
        const imageData = new Uint8Array(image.data);

        const edges = matRetain(frame.edgesImage);
        const edgesData = new Uint8Array(edges.data);

        // const debugImage = greyscale.clone();
        // imShow(debugImage);
        // debugImage.delete();

        //const start = performance.now();

        //setup input once
        const scale = image.cols / edges.cols;
        service.configureInput({
                seedPoint:{x:scale * initialPosition.x,
                           y:scale * initialPosition.y},
                image: describeImageInput(image),
                edgesImage: describeImageInput(edges)
            }
        ).then(()=>{
            return service.fastSegment(imageData, edgesData);
        }).then(result=>{
            //console.log(`Segmentation result after`, performance.now() - start);
            if (!result) {
                return;
            }

            if (result.debugImage) {
                const debugImage = deserializeImage(result.debugImage);
                imShow(debugImage);
                debugImage.delete();
            }

            const mask = deserializeImage(result.mask);
            // console.log(`Mask size: ${mask.cols}x${mask.rows}`);
            // const scale = edges.cols / mask.cols;
            // const roi = {
            //     x:scale * result.roi.x, y:scale * result.roi.y,
            //     width:scale * result.roi.width, height:scale * result.roi.height
            // };
            // cv.resize(mask, mask, {width:edges.cols, height:edges.rows});
            wall.setMask(mask, initialPosition, result.roi, frame.index);
            matRelease(mask);

            wall.setLightingFactors(result.lightingFactor, result.lightingOffset, frame.index);

        }).finally(()=>{
            wall.maskFrameIndex = frame.index;
            this._modelBusy = false;
            matRelease(image);
            matRelease(edges);
        });

        //console.log(`Segmentation call performed after`, performance.now() - start);
    }

    debug(canvas:HTMLCanvasElement):void {

    }

    destroy() {
        this._modelBusy = true;
        this.unwireModelThread().then(()=>{
            console.log("Segmentation thread unloaded");
        })
    }
}